import { ITicket, ITicketSubTicket, TaiXiuBetType, normalizeTaiXiuTicketBetType, taiXiuMarketNameBetType } from '@/utils/index.model';

/*
 * Multi-selection tickets: one ticket for a round, with one sub-ticket per
 * selection (`sub_tickets` on the tickets endpoint). The parent carries the
 * totals (stake, W/L); each sub-ticket its own selection, odds and W/L.
 */

// `status` on the tickets endpoint: 4 is settled.
export const TICKET_STATUS_SETTLED = 4;
// A cancelled or voided bet is finished too, and says so in `result`.
const FINISHED_RESULTS = ['cancelled', 'void'];

/*
 * The ticket's sub-tickets, each completed from its parent where the payload
 * leaves a field out: `bet_type` (needed to badge a numeric Tài Xỉu selection)
 * and the creation time. A settled parent settles them all: the backend leaves
 * each sub-ticket at its placed status (2) even once its result is in, so they
 * read PENDING under a settled ticket.
 */
export const getSubTickets = (ticket?: Partial<ITicket> | null): ITicketSubTicket[] => {
  if (!ticket || !Array.isArray(ticket.subTickets)) return [];
  const parentSettled = Number(ticket.status) === TICKET_STATUS_SETTLED;
  return ticket.subTickets
    .filter((sub) => !!sub)
    .map((sub, index) => ({
      ...sub,
      betType: subTicketBetType(ticket, sub, index),
      createdDateTime: sub.createdDateTime || ticket.createdDateTime,
      ...(parentSettled ? { status: TICKET_STATUS_SETTLED } : {}),
    }));
};

// More than one selection. A ticket with a single sub-ticket reads as a plain bet.
export const isMultiSelectionTicket = (ticket?: Partial<ITicket> | null): boolean =>
  getSubTickets(ticket).length > 1;

export const isTicketPending = (ticket: { status?: number; result?: string }): boolean =>
  Number(ticket.status) !== TICKET_STATUS_SETTLED &&
  !FINISHED_RESULTS.includes(String(ticket.result || '').toLowerCase());

/*
 * One part of the tickets endpoint's selection summary, in any of its forms:
 *  - "big:1.97": the selection and its odds;
 *  - "triple:5:121", "total_13:13:9": the market first, then those two;
 *  - "%!(STRING=ODD_EVEN)odd:1.97": the same, with the market mangled by a
 *    backend formatting bug.
 * The market, when there is one, says what a bare "3" or "5" was bet on.
 */
const marketBetType = (market: string, selection: string): TaiXiuBetType | undefined =>
  taiXiuMarketNameBetType(market, selection);

const splitSelectionOdds = (selection: unknown): { selection: string; odds?: number; betType?: TaiXiuBetType } => {
  let text = String(selection === undefined || selection === null ? '' : selection).trim();
  let market = '';
  const mangled = text.match(/^%!\(STRING=([^)]*)\)(.*)$/i);
  if (mangled) {
    market = mangled[1];
    text = mangled[2];
  }
  const parts = text.split(':').map((part) => part.trim());
  const oddsText = parts.length >= 2 ? parts[parts.length - 1] : '';
  const hasOddsPart = /^\d+(?:\.\d+)?$/.test(oddsText);
  if (!hasOddsPart) return { selection: text };
  if (parts.length === 3 && !market) market = parts[0];
  const value = parts[parts.length - 2];
  const betType = market ? marketBetType(market, value) : undefined;
  return { selection: value, odds: Number(oddsText), ...(betType !== undefined ? { betType } : {}) };
};

// The parent summary's parts, so a sub-ticket can borrow the market of its own.
const summaryParts = (ticket: Partial<ITicket>) =>
  String(ticket.selection === undefined || ticket.selection === null ? '' : ticket.selection)
    .split('|')
    .map((part) => part.trim())
    .filter((part) => !!part)
    .map(splitSelectionOdds);

// A bet type that names a Tài Xỉu market for this selection (not 0 / missing).
const isKnownBetType = (betType: unknown, selection: unknown): boolean =>
  betType !== undefined && betType !== null && normalizeTaiXiuTicketBetType(betType, selection) !== undefined;

/*
 * A sub-ticket's bet type: its own, else the parent's, else the market named
 * for it in the parent's summary (matched by selection, then by position).
 */
const subTicketBetType = (
  ticket: Partial<ITicket>,
  sub: { selection?: unknown; betType?: number },
  index: number
): number | undefined => {
  if (isKnownBetType(sub.betType, sub.selection)) return sub.betType;
  if (isKnownBetType(ticket.betType, sub.selection)) return ticket.betType;
  const parts = summaryParts(ticket);
  const selection = String(sub.selection === undefined || sub.selection === null ? '' : sub.selection).toLowerCase();
  const match =
    parts.find((part) => part.betType !== undefined && part.selection.toLowerCase() === selection) || parts[index];
  if (match && match.betType !== undefined) return match.betType;
  const fromOdds = faceBetTypeFromOdds(sub.selection, (sub as { odds?: unknown }).odds);
  if (fromOdds !== undefined) return fromOdds;
  return sub.betType !== undefined && sub.betType !== null ? sub.betType : ticket.betType;
};

/*
 * Last resort for a lone die face (1..6) with no bet type anywhere: the parent
 * has `bet_type: 0`, the sub-tickets none, and the backend cuts the summary
 * short ("total_13:13:9|triple:5:121|bi..."), so later selections lose their
 * market. The odds still tell the two face markets apart -- a single pays about
 * 1:1 (1.97), a triple about 1:120 (121). Anything in between stays unknown.
 */
const faceBetTypeFromOdds = (selection: unknown, odds: unknown): TaiXiuBetType | undefined => {
  const face = Number(selection);
  const value = Number(odds);
  if (!Number.isInteger(face) || face < 1 || face > 6 || !(value > 1)) return undefined;
  if (value < 4) return TaiXiuBetType.single;
  if (value >= 50) return TaiXiuBetType.triple;
  return undefined;
};

const hasOdds = (odds: unknown): boolean => Number(odds) > 0;

/*
 * A summary selection's bet type: the parent's when it fits the selection,
 * else read from the selection itself (a 4..17 is a total, Big/Small and
 * Odd/Even their markets).
 */
const summaryBetType = (parentBetType: unknown, selection: string): number | undefined => {
  const own = normalizeTaiXiuTicketBetType(parentBetType, selection);
  const total = Number(selection);
  const isTotal = Number.isInteger(total) && total >= 4 && total <= 17;
  if (own !== undefined && (!isTotal || own === TaiXiuBetType.total4 + total - 4)) return own;
  if (isTotal) return TaiXiuBetType.total4 + total - 4;
  const name = selection.toLowerCase();
  if (['big', 'small', 'tai', 'xiu', 'tài', 'xỉu'].includes(name)) return TaiXiuBetType.bigSmall;
  if (['odd', 'even', 'le', 'chan', 'lẻ', 'chẵn'].includes(name)) return TaiXiuBetType.oddEven;
  return own;
};

/*
 * Selections read from a "4:31|17:31" summary, for a ticket without
 * `sub_tickets`. The summary has no stake or W/L per selection, so those stay
 * empty (`derived`) and the screens show "—"; status follows the parent.
 */
export const subTicketsFromSummary = (ticket: Partial<ITicket>): ITicketSubTicket[] => {
  const text = String(ticket.selection === undefined || ticket.selection === null ? '' : ticket.selection);
  if (!text.includes('|')) return [];
  return text
    .split('|')
    .map((part) => part.trim())
    .filter((part) => !!part)
    .map((part, index) => {
      const parsed = splitSelectionOdds(part);
      return {
        id: `${ticket.id || 'ticket'}-${index}`,
        selection: parsed.selection,
        odds: parsed.odds !== undefined ? parsed.odds : '',
        amount: '',
        result: ticket.result || '',
        winLossAmount: '',
        status: ticket.status,
        betType:
          parsed.betType !== undefined
            ? parsed.betType
            : summaryBetType(ticket.betType, parsed.selection) ??
              faceBetTypeFromOdds(parsed.selection, parsed.odds),
        createdDateTime: ticket.createdDateTime,
        derived: true,
      };
    });
};

/*
 * A ticket as the screens read it. A single-selection ticket now comes back
 * with one sub-ticket and a summary on the parent ("BIG:1.97", odds 0); it
 * takes its selection, bet type and odds from that sub-ticket. A summary left
 * on any ticket is split into selection and odds. Multi-selection tickets keep
 * their parent as it is: their selections are read from `sub_tickets`.
 */
export const normalizeTicket = <T extends Partial<ITicket>>(ticket: T, deriveSelections = true): T => {
  if (!ticket || typeof ticket !== 'object') return ticket;
  const subs = Array.isArray(ticket.subTickets) ? ticket.subTickets.filter((sub) => !!sub) : [];
  if (subs.length > 1) return ticket;

  // No `sub_tickets` (the endpoint does not send them yet): a summary of two
  // or more selections ("4:31|17:31") is a multi-selection ticket all the same.
  if (!subs.length && deriveSelections) {
    const derived = subTicketsFromSummary(ticket);
    if (derived.length > 1) return { ...ticket, subTickets: derived };
  }

  const sub = subs[0];
  const source = sub && sub.selection !== undefined && sub.selection !== null ? sub.selection : ticket.selection;
  const parsed = splitSelectionOdds(source);
  const odds = [ticket.odds, sub && sub.odds, parsed.odds].find(hasOdds);

  // The parent's summary names the market when the bet types do not.
  const fromSummary = splitSelectionOdds(ticket.selection);
  const betType = sub
    ? subTicketBetType({ ...ticket, selection: ticket.selection }, sub, 0)
    : isKnownBetType(ticket.betType, parsed.selection) || parsed.betType === undefined
    ? ticket.betType
    : parsed.betType;

  return {
    ...ticket,
    selection: parsed.selection,
    betType: betType !== undefined ? betType : fromSummary.betType,
    ...(odds !== undefined ? { odds } : {}),
  };
};

// `deriveSelections: false` keeps "4:31|17:31" tickets as they are (the rail's CURRENT tab).
export const normalizeTickets = <T extends Partial<ITicket>>(tickets: T[], deriveSelections = true): T[] =>
  Array.isArray(tickets) ? tickets.map((ticket) => normalizeTicket(ticket, deriveSelections)) : [];
